/**
 * VGrove 标准化 Action 工具
 * 提供基于 React Router v7+ action 模式的标准化表单处理和数据变更
 * 替代原有的复杂中间件系统
 */

import { redirect, type ActionFunction, type ActionFunctionArgs } from 'react-router';
import { debug } from './debug';
import type { AuthCheck } from './loaders';
import { isAsyncFunction } from './utils';
import type { BaseUser } from './types';
/**
 * 表单验证错误
 */
export interface ValidationError {
  field: string;
  message: string;
}

/**
 * Action 响应类型
 */
export interface ActionResponse<T = unknown> {
  success: boolean;
  data?: T;
  errors?: ValidationError[];
  message?: string;
}

/**
 * 表单验证函数类型
 */
export type FormValidator = (formData: FormData) => Promise<ValidationError[]> | ValidationError[];

/**
 * 数据处理函数类型
 */
export type DataProcessor<TUser extends BaseUser = BaseUser, TReturn = unknown> = (formData: FormData, user?: TUser) => Promise<TReturn> | TReturn;

/**
 * Action 配置选项
 */
export interface ActionOptions<TUser extends BaseUser = BaseUser, TReturn = unknown> {
  /** 表单验证函数 */
  validator?: FormValidator;
  /** 数据处理函数 */
  processor?: DataProcessor<TUser, TReturn>;
  /** 成功后的重定向路径 */
  successRedirect?: string;
  /** 失败后的重定向路径 */
  errorRedirect?: string;
  /** 是否需要认证 */
  requireAuth?: boolean;
  /** 需要的角色 */
  requiredRoles?: string[];
  /** 需要的权限 */
  requiredPermissions?: string[];
  /** 认证检查函数 */
  authCheck?: AuthCheck<TUser>;
}

/**
 * 默认配置
 */
const DEFAULT_ACTION_OPTIONS: Partial<ActionOptions> = {
  requireAuth: false,
  authCheck: () => null
};

/**
 * 全局 action 配置
 */
let globalActionOptions: Partial<ActionOptions<BaseUser>> = {};

/**
 * 配置全局 action 选项
 */
export function configureActions<TUser extends BaseUser = BaseUser>(options: Partial<ActionOptions<TUser>>): void {
  globalActionOptions = { ...globalActionOptions, ...options } as any;
  debug.action('Action 全局配置已更新', options);
}

/**
 * 获取合并后的配置
 */
function getMergedActionOptions<TUser extends BaseUser = BaseUser, TReturn = unknown>(options?: Partial<ActionOptions<TUser, TReturn>>): ActionOptions<TUser, TReturn> {
  return {
    ...DEFAULT_ACTION_OPTIONS,
    ...globalActionOptions,
    ...options
  } as ActionOptions<TUser, TReturn>;
}

/**
 * 从 FormData 中提取数据
 */
function extractFormData(formData: FormData): Record<string, unknown> {
  const data: Record<string, unknown> = {};

  for (const [key, value] of formData.entries()) {
    if (data[key]) {
      // 处理多值字段（如复选框）
      if (Array.isArray(data[key])) {
        data[key].push(value);
      } else {
        data[key] = [data[key], value];
      }
    } else {
      data[key] = value;
    }
  }

  return data;
}

/**
 * 检查用户权限
 */
async function checkPermissions<TUser extends BaseUser = BaseUser>(
  user: TUser,
  requiredRoles?: string[],
  requiredPermissions?: string[]
): Promise<boolean> {
  if (!user) return false;

  // 检查角色
  if (requiredRoles && requiredRoles.length > 0) {
    const userRoles = user.roles || [];
    const hasRole = requiredRoles.some(role => userRoles.includes(role));
    if (!hasRole) return false;
  }

  // 检查权限
  if (requiredPermissions && requiredPermissions.length > 0) {
    const userPermissions = user.permissions || [];
    const hasPermission = requiredPermissions.some(permission => userPermissions.includes(permission));
    if (!hasPermission) return false;
  }

  return true;
}

/**
 * 创建基础 action
 * 处理表单提交的基础逻辑
 */
export function createAction<TUser extends BaseUser = BaseUser, TReturn = unknown>(
  options: ActionOptions<TUser, TReturn>
): ActionFunction {
  return async ({ request }: ActionFunctionArgs) => {
    const config = getMergedActionOptions<TUser, TReturn>(options);

    debug.action('执行 action', {
      method: request.method,
      url: request.url,
      requireAuth: config.requireAuth
    });

    try {
      // 检查认证
      let user: TUser = {} as any;
      if (config.requireAuth && config.authCheck) {
        if (isAsyncFunction(config.authCheck))
          user = await config.authCheck();
        else
          user = config.authCheck() as any;
        if (!user) {
          debug.action('用户未认证');
          return Response.json({
            success: false,
            message: '用户未认证'
          }, { status: 401 });
        }
      }

      // 检查权限
      if (user) {
        const hasPermission = await checkPermissions(
          user,
          config.requiredRoles,
          config.requiredPermissions
        );

        if (!hasPermission) {
          debug.action('权限不足', {
            userId: user.id,
            requiredRoles: config.requiredRoles,
            requiredPermissions: config.requiredPermissions
          });
          return Response.json({
            success: false,
            message: '权限不足'
          }, { status: 403 });
        }
      }

      // 解析表单数据
      const formData = await request.formData();
      const extractedData = extractFormData(formData);

      debug.action('表单数据已解析', { fields: Object.keys(extractedData) });

      // 验证表单
      if (config.validator) {
        const validationErrors = await config.validator(formData);
        if (validationErrors.length > 0) {
          debug.action('表单验证失败', { errors: validationErrors });
          return Response.json({
            success: false,
            errors: validationErrors,
            message: '表单验证失败'
          }, { status: 400 });
        }
      }

      // 处理数据
      let result: TReturn | undefined;
      if (config.processor) {
        if (isAsyncFunction(config.processor)) {
          result = await config.processor(formData, user || undefined);
        } else {
          result = config.processor(formData, user || undefined) as any;
        }
        debug.action('数据处理完成', { hasResult: !!result });
      }

      // 成功响应
      const response: ActionResponse<TReturn> = {
        success: true,
        data: result,
        message: '操作成功'
      };

      // 处理重定向
      if (config.successRedirect) {
        debug.action('重定向到成功页面', { path: config.successRedirect });
        throw redirect(config.successRedirect);
      }

      debug.action('Action 执行成功');
      return Response.json(response);

    } catch (error) {
      // 如果是重定向，直接抛出
      if (error instanceof Response) {
        throw error;
      }

      debug.action('Action 执行失败', { error: error instanceof Error ? error.message : String(error) });

      // 处理错误重定向
      if (config.errorRedirect) {
        throw redirect(config.errorRedirect);
      }

      return Response.json({
        success: false,
        message: error instanceof Error ? error.message : '操作失败'
      }, { status: 500 });
    }
  };
}

/**
 * 创建登录 action
 */
export function createLoginAction<TUser extends BaseUser = BaseUser, TCredentials = { username: string; password: string }, TReturn = unknown>(
  loginProcessor: (credentials: TCredentials) => Promise<{ user: TUser; token: string }>,
  options?: Partial<ActionOptions<TUser, TReturn>>
): ActionFunction {
  return createAction<TUser, TReturn>({
    ...options,
    requireAuth: false,
    validator: async (formData) => {
      const errors: ValidationError[] = [];

      const username = formData.get('username') as string;
      const password = formData.get('password') as string;

      if (!username || username.trim() === '') {
        errors.push({ field: 'username', message: '用户名不能为空' });
      }

      if (!password || password.trim() === '') {
        errors.push({ field: 'password', message: '密码不能为空' });
      }

      return errors;
    },
    processor: async (formData) => {
      const username = formData.get('username') as string;
      const password = formData.get('password') as string;

      const result = await loginProcessor({ username, password } as TCredentials);

      // 存储认证信息到本地存储
      if (typeof window !== 'undefined') {
        localStorage.setItem('auth_user', JSON.stringify(result.user));
        localStorage.setItem('auth_token', result.token);
      }

      return result as TReturn;
    }
  });
}

/**
 * 创建注册 action
 */
export function createRegisterAction<TUser extends BaseUser = BaseUser, TReturn = unknown>(
  registerProcessor: (userData: Record<string, unknown>) => Promise<{ user: TUser; token: string }>,
  options?: Partial<ActionOptions<TUser, TReturn>>
): ActionFunction {
  return createAction<TUser, TReturn>({
    ...options,
    requireAuth: false,
    validator: async (formData) => {
      const errors: ValidationError[] = [];

      const username = formData.get('username') as string;
      const email = formData.get('email') as string;
      const password = formData.get('password') as string;
      const confirmPassword = formData.get('confirmPassword') as string;

      if (!username || username.trim() === '') {
        errors.push({ field: 'username', message: '用户名不能为空' });
      }

      if (!email || email.trim() === '') {
        errors.push({ field: 'email', message: '邮箱不能为空' });
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        errors.push({ field: 'email', message: '邮箱格式不正确' });
      }

      if (!password || password.length < 6) {
        errors.push({ field: 'password', message: '密码至少需要6个字符' });
      }

      if (password !== confirmPassword) {
        errors.push({ field: 'confirmPassword', message: '两次输入的密码不一致' });
      }

      return errors;
    },
    processor: async (formData) => {
      const userData = extractFormData(formData);
      delete userData.confirmPassword; // 移除确认密码字段

      const result = await registerProcessor(userData);

      // 存储认证信息到本地存储
      if (typeof window !== 'undefined') {
        localStorage.setItem('auth_user', JSON.stringify(result.user));
        localStorage.setItem('auth_token', result.token);
      }

      return result as TReturn;
    }
  });
}

/**
 * 创建登出 action
 */
export function createLogoutAction<TUser extends BaseUser = BaseUser, TReturn = unknown>(
  logoutProcessor?: () => Promise<void>,
  options?: Partial<ActionOptions<TUser, TReturn>>
): ActionFunction {
  return createAction<TUser, TReturn>({
    ...options,
    requireAuth: true,
    processor: async () => {
      // 执行自定义登出逻辑
      if (logoutProcessor) {
        await logoutProcessor();
      }

      // 清除本地存储
      if (typeof window !== 'undefined') {
        localStorage.removeItem('auth_user');
        localStorage.removeItem('auth_token');
        localStorage.removeItem('auth_refresh_token');
      }

      return { message: '登出成功' } as TReturn;
    },
    successRedirect: '/login'
  });
}

/**
 * 创建 CRUD action
 */
export function createCrudAction<TUser extends BaseUser = BaseUser, TReturn = unknown>(
  operation: 'create' | 'update' | 'delete',
  processor: DataProcessor<TUser, TReturn>,
  options?: Partial<ActionOptions<TUser, TReturn>>
): ActionFunction {
  const baseValidator: FormValidator = async (formData) => {
    const errors: ValidationError[] = [];

    if (operation === 'delete') {
      const id = formData.get('id');
      if (!id) {
        errors.push({ field: 'id', message: 'ID 不能为空' });
      }
    }

    return errors;
  };

  return createAction<TUser, TReturn>({
    requireAuth: true,
    ...options,
    validator: options?.validator || baseValidator,
    processor
  });
}

/**
 * 创建文件上传 action
 */
export function createFileUploadAction<TUser extends BaseUser = BaseUser, TReturn = unknown>(
  uploadProcessor: (files: File[], formData: FormData, user?: TUser) => Promise<{ urls: string[] }>,
  options?: Partial<ActionOptions<TUser, TReturn>>
): ActionFunction {
  return createAction<TUser, TReturn>({
    ...options,
    requireAuth: true,
    validator: async (formData) => {
      const errors: ValidationError[] = [];

      const files = formData.getAll('files') as File[];
      if (files.length === 0) {
        errors.push({ field: 'files', message: '请选择要上传的文件' });
      }

      // 检查文件大小和类型
      for (const file of files) {
        if (file.size > 10 * 1024 * 1024) { // 10MB
          errors.push({ field: 'files', message: `文件 ${file.name} 超过10MB限制` });
        }
      }

      return errors;
    },
    processor: async (formData, user) => {
      const files = formData.getAll('files') as File[];
      const result = await uploadProcessor(files, formData, user);
      return result as TReturn;
    }
  });
}

/**
 * 组合多个 action
 * 按顺序执行多个 action 的处理逻辑
 */
export function combineActions(...actions: ActionFunction[]): ActionFunction {
  return async (args: ActionFunctionArgs) => {
    debug.action('执行组合 action', { count: actions.length });

    let combinedResult: Record<string, unknown> = {};

    for (let i = 0; i < actions.length; i++) {
      const action = actions[i];
      const result = await action(args);

      // 如果是重定向响应，直接返回
      if (result instanceof Response && result.status >= 300 && result.status < 400) {
        return result;
      }

      // 如果是 JSON 响应，合并结果
      if (result instanceof Response) {
        const data = await result.json();
        combinedResult = { ...combinedResult, ...data };
      } else if (result) {
        combinedResult = { ...combinedResult, ...result };
      }
    }

    debug.action('组合 action 执行完成');
    return Response.json(combinedResult);
  };
}